import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  LayoutDashboard,
  Send,
  FileCode,
  Users,
  List,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  ChevronLeft,
  ChevronDown
} from 'lucide-react';
import LogoMark from '../components/LogoMark';

export const DashboardLayout: React.FC = () => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex justify-center items-center">
        <div className="w-12 h-12 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = process.env.REACT_APP_QQ_PORTAL_URL || 'http://localhost:7001/my-tools';
    return null;
  }

  const navItems = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { name: 'Campaigns', path: '/campaigns', icon: Send },
    { name: 'Templates', path: '/templates', icon: FileCode },
    { name: 'Recipients', path: '/recipients', icon: Users },
    { name: 'Recipient Lists', path: '/lists', icon: List },
    { name: 'Analytics Reports', path: '/analytics', icon: BarChart3 }
  ];

  // Admin-only navigation
  if (user?.role === 'ADMIN') {
    navItems.push({ name: 'System Settings', path: '/settings', icon: Settings });
  }

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleCollapse = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };



  return (
    <div className="min-h-screen flex bg-[#f8fafc] dark:bg-[#0b111e] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      {/* Sidebar - Desktop */}
      <aside className={`hidden md:flex flex-col transition-all duration-300 ${isSidebarCollapsed ? 'w-16' : 'w-64'} bg-salty-card border-r border-salty-border shrink-0 sticky top-0 h-screen overflow-hidden`}>
        {/* Brand */}
        <div className={`h-[72px] flex items-center border-b border-salty-border px-4 transition-all duration-300 ${isSidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
          <div 
            className={`flex items-center gap-3 overflow-hidden cursor-pointer ${isSidebarCollapsed ? 'justify-center w-full' : ''}`}
            onClick={isSidebarCollapsed ? toggleCollapse : undefined}
            title={isSidebarCollapsed ? "Expand Sidebar" : ""}
          >
            <LogoMark size={32} className="shrink-0" />
            {!isSidebarCollapsed && (
              <span className="font-bold text-base tracking-wide bg-gradient-to-r from-slate-900 via-slate-700 to-slate-500 dark:from-white dark:via-slate-100 dark:to-slate-400 bg-clip-text text-transparent whitespace-nowrap">
                Email Marketing
              </span>
            )}
          </div>
          {!isSidebarCollapsed && (
            <button
              onClick={toggleCollapse}
              className="p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors shrink-0"
              title="Collapse Sidebar"
            >
              <ChevronLeft size={16} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center rounded-xl transition-all duration-200 group font-medium ${
                  isSidebarCollapsed ? 'justify-center p-3' : 'gap-3 px-4 py-3 text-sm font-semibold'
                } ${
                  isActive
                    ? 'bg-[#1264d1]/10 text-[#1264d1] dark:text-[#3b82f6]'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5'
                }`}
                title={isSidebarCollapsed ? item.name : undefined}
              >
                <Icon size={18} className={isActive ? 'text-[#1264d1] dark:text-[#3b82f6]' : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200'} />
                {!isSidebarCollapsed && <span className="truncate">{item.name}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Standalone Logout Button at bottom matching Influencer Marketing */}
        <div className={`p-4 border-t border-transparent flex flex-col gap-4 ${isSidebarCollapsed ? 'items-center' : ''}`}>
          <button
            onClick={() => logout()}
            className={`flex items-center justify-center bg-slate-100 dark:bg-slate-800/60 hover:bg-rose-500/10 text-slate-500 dark:text-slate-400 hover:text-rose-600 border border-transparent rounded-xl transition-all ${
              isSidebarCollapsed ? 'p-2.5 w-9 h-9' : 'w-full gap-2 px-4 py-2.5 text-xs font-semibold'
            }`}
            title="Log out"
          >
            <LogOut size={14} className="shrink-0" />
            {!isSidebarCollapsed && 'Log Out'}
          </button>
        </div>
      </aside>

      {/* Main Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden relative">
        {/* Header - Mobile & Desktop Top Bar */}
        <header className="h-[72px] flex items-center justify-between px-8 bg-salty-card border-b border-salty-border relative z-20">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="flex items-center gap-2 md:hidden">
              <LogoMark size={28} />
              <span className="font-bold text-base tracking-wide bg-gradient-to-r from-slate-900 to-slate-500 dark:from-white dark:to-slate-400 bg-clip-text text-transparent whitespace-nowrap">Email Marketing</span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-200/50 dark:bg-white/5 border border-transparent rounded-full px-3 py-1 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-accent-emerald animate-pulse"></span>
              SES Service: Connected
            </div>

            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="w-10 h-10 rounded-xl border border-salty-border bg-salty-card hover:bg-salty-subtle transition-all flex items-center justify-center text-slate-500 dark:text-slate-400 hover:text-[#1264d1] dark:hover:text-[#3b82f6] shadow-sm cursor-pointer"
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* User Profile Dropdown matching Influencer Marketing */}
            <div className="relative">
              <div 
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                className="flex items-center gap-3 px-3 py-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-white/5 transition-all cursor-pointer select-none"
              >
                <div className="w-9 h-9 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-sm shadow-sm shrink-0">
                  {user?.name?.charAt(0).toUpperCase() || 'D'}
                </div>
                <div className="hidden sm:flex flex-col text-left">
                  <span className="font-bold text-slate-800 dark:text-white text-sm leading-tight">{user?.name || 'Demo'}</span>
                  <span className={`px-1.5 py-0.5 rounded border text-[9px] font-black uppercase tracking-wider w-fit mt-0.5 leading-none ${
                    user?.role === 'ADMIN'
                      ? 'bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-500'
                      : 'bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400'
                  }`}>
                    {user?.role || 'USER'}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400 shrink-0" />
              </div>

              {/* Profile Dropdown Menu */}
              {isProfileDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-slate-900 border border-salty-border rounded-2xl shadow-xl py-2 z-50 animate-fadeIn">
                  <div className="px-4 py-2 border-b border-salty-border">
                    <p className="text-xs text-slate-400">Signed in as</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{user?.email || 'admin@example.com'}</p>
                  </div>
                  <button
                    onClick={() => {
                      setIsProfileDropdownOpen(false);
                      logout();
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-500 dark:text-slate-400 hover:text-rose-600 hover:bg-rose-500/5 text-left transition-all font-semibold"
                  >
                    <LogOut className="w-4 h-4" />
                    Log Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Mobile Navigation Drawer */}
        {isMobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-30 flex">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)}></div>
            
            {/* Drawer */}
            <aside className="relative flex flex-col w-64 max-w-xs bg-white dark:bg-slate-950 border-r border-transparent h-full z-40 p-4">
              <div className="flex items-center justify-between pb-6 mb-6 border-b border-salty-border">
                <div className="flex items-center gap-2">
                  <LogoMark size={28} />
                  <span className="font-bold text-base tracking-wide bg-gradient-to-r from-slate-900 to-slate-500 dark:from-white dark:to-slate-400 bg-clip-text text-transparent whitespace-nowrap">Email Marketing</span>
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5"
                >
                  <X size={20} />
                </button>
              </div>

              <nav className="flex-1 space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname.startsWith(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={handleLinkClick}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                        isActive
                          ? 'bg-primary-500/10 text-primary-500 border border-primary-500/20'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5 border border-transparent'
                      }`}
                    >
                      <Icon size={18} />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>

              <div className="pt-4 border-t border-transparent space-y-3">
                <div className="text-sm font-semibold text-slate-800 dark:text-white px-2">{user?.name ? user.name.split(' ')[0] : ''}</div>
                <button
                  onClick={() => {
                    handleLinkClick();
                    logout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-slate-500 dark:text-slate-400 hover:text-accent-rose hover:bg-accent-rose/5 border border-transparent hover:border-accent-rose/10 font-medium text-sm transition-all duration-200"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            </aside>
          </div>
        )}

        {/* Page Content Viewport */}
        <main className="flex-1 p-6 md:p-8 overflow-y-auto relative z-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
